import { Controller } from "@hotwired/stimulus";

// GDPR export: single POST returns JSON (password + zip as base64). Nothing is stored on disk or in cache.
export default class extends Controller {
  static targets = ["password", "exportedAt", "status", "button", "result"];
  static values = { url: String };

  connect() {
    this.beforeCache = () => this.clearSecrets();
    document.addEventListener("turbo:before-cache", this.beforeCache);
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache);
    this.clearSecrets();
  }

  clearSecrets() {
    if (this.hasPasswordTarget) this.passwordTarget.value = "";
    if (this.hasExportedAtTarget) this.exportedAtTarget.textContent = "";
    if (this.hasStatusTarget) this.statusTarget.textContent = "";
    if (this.hasResultTarget) this.resultTarget.classList.add("hidden");
  }

  async export(event) {
    event.preventDefault();
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (!token) return;

    this.buttonTarget.disabled = true;
    this.statusTarget.textContent = "Generating export…";
    try {
      const res = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": token,
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || res.statusText);
      }
      const data = await res.json();
      this.passwordTarget.value = data.password;
      if (this.hasExportedAtTarget) {
        this.exportedAtTarget.textContent = data.exported_at_label || data.exported_at || "";
      }
      this.resultTarget.classList.remove("hidden");
      this.downloadZip(data.zip_base64, data.filename);
      this.statusTarget.textContent =
        "ZIP download started. Copy the password above before you leave this page — it is not stored on the server.";
    } catch (e) {
      this.statusTarget.textContent = "Export failed.";
      alert(e.message);
    } finally {
      this.buttonTarget.disabled = false;
    }
  }

  downloadZip(base64, suggestedName) {
    const blob = this.base64ToBlob(base64);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName || "gdpr-export.zip";
    a.click();
    URL.revokeObjectURL(url);
  }

  base64ToBlob(base64) {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: "application/zip" });
  }
}
